// Versão da aplicação - Atualizar a cada deploy
// Formato: MAJOR.MINOR.PATCH-BUILD
export const APP_VERSION = '2.2.0';

// Timestamp do build (atualizado automaticamente pelo CI ou manualmente)
export const BUILD_TIMESTAMP = Date.now();

// Chave para armazenar versão no localStorage
const VERSION_KEY = 'jornyx_app_version';
const LAST_CHECK_KEY = 'jornyx_last_version_check';

/**
 * Verifica se há uma nova versão e força atualização se necessário
 * Retorna true se uma atualização foi detectada
 */
export const checkForUpdates = () => {
  try {
    const storedVersion = localStorage.getItem(VERSION_KEY);
    const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
    const now = Date.now();
    
    // Se não há versão armazenada, é primeiro acesso
    if (!storedVersion) {
      localStorage.setItem(VERSION_KEY, APP_VERSION);
      localStorage.setItem(LAST_CHECK_KEY, now.toString());
      return false;
    }
    
    // Se a versão mudou, forçar atualização
    if (storedVersion !== APP_VERSION) {
      console.log(`[Version] Nova versão detectada: ${storedVersion} -> ${APP_VERSION}`);
      forceUpdate();
      return true;
    }
    
    return false;
  } catch (e) {
    console.error('Erro ao verificar versão:', e);
    return false;
  }
};

/**
 * Força atualização limpando caches e recarregando
 */
export const forceUpdate = async () => {
  console.log('[Version] Forçando atualização...');
  
  try {
    // Atualizar versão no localStorage
    localStorage.setItem(VERSION_KEY, APP_VERSION);
    localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
    
    // Limpar Service Worker caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => {
          console.log(`[Version] Limpando cache: ${cacheName}`);
          return caches.delete(cacheName);
        })
      );
    }
    
    // Desregistrar Service Workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        console.log('[Version] Desregistrando SW:', registration.scope);
        await registration.unregister();
      }
    }
    
    // Limpar localStorage seletivamente (manter credenciais)
    const keysToKeep = ['jornyx_app_version', 'jornyx_last_version_check'];
    const allKeys = Object.keys(localStorage);
    allKeys.forEach(key => {
      if (!keysToKeep.includes(key) && key.startsWith('jornyx_cache_')) {
        localStorage.removeItem(key);
      }
    });
    
    // Recarregar a página ignorando cache
    window.location.reload(true);
  } catch (e) {
    console.error('Erro ao forçar atualização:', e);
    // Fallback: apenas recarregar
    window.location.reload(true);
  }
};

/**
 * Retorna informações de versão formatadas
 */
export const getVersionInfo = () => {
  return {
    version: APP_VERSION,
    buildTime: new Date(BUILD_TIMESTAMP).toISOString(),
    userAgent: navigator.userAgent,
    online: navigator.onLine
  };
};

/**
 * Hook para verificar atualizações periodicamente
 * Usar em App.js ou componente raiz
 */
export const setupAutoUpdate = (intervalMinutes = 5) => {
  // Verificar imediatamente
  checkForUpdates();
  
  // Verificar periodicamente
  const interval = setInterval(() => {
    if (document.visibilityState === 'visible') {
      // Fazer uma requisição para verificar se há nova versão no servidor
      fetch('/version.json?' + Date.now())
        .then(res => res.json())
        .then(data => {
          if (data.version && data.version !== APP_VERSION) {
            console.log(`[Version] Nova versão disponível no servidor: ${data.version}`);
            // Notificar usuário ou forçar atualização
            if (window.confirm('Nova versão disponível! Deseja atualizar agora?')) {
              forceUpdate();
            }
          }
        })
        .catch(() => {
          // Silenciosamente ignorar erros de rede
        });
    }
  }, intervalMinutes * 60 * 1000);
  
  // Verificar quando a aba ficar visível novamente
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForUpdates();
    }
  });
  
  return () => clearInterval(interval);
};

export default {
  APP_VERSION,
  BUILD_TIMESTAMP,
  checkForUpdates,
  forceUpdate,
  getVersionInfo,
  setupAutoUpdate
};
